import React, { useState, useCallback, useEffect } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown, Button, Spinner } from "react-bootstrap";
import { sanitizeInput, sanitizePhoneNumber, sanitizeNumericInput } from "../common/security-utils";
import CommonDropdown from "../common/common_dropDown";
import deleteIcon from "../../assets/svg/delete-po.svg";
import {
  submitTripsAPI,
  updateTripsAPI,
  getTripsEditApi,
  getVehicleDetailApi,
  getStopsApi,
  getSortedStopsApi,
  getDefaultMilestoneApi,
} from "./trips-api";
import "react-datepicker/dist/react-datepicker.css";
import { v4 as uuidv4 } from "uuid";
import CommonAsyncMultiSelect from "../common/CommonAsyncMultiSelect";
import CustomDateRangePicker from "../common/dateRangeCustom";
import { format } from "date-fns";
import CommonStaticDropdown from "../common/common_static_dropdown";
import { ReactComponent as DownArrowIc } from "../../assets/svg/down_arrow_ics.svg";
import totalAmtIc from "../../assets/svg/total_amount.svg";
import totalKMIc from "../../assets/svg/total_km.svg";
import totalVolumeIc from "../../assets/svg/total_volume.svg";
import totalPackageIc from "../../assets/svg/total_package.svg";
import totalWeightIc from "../../assets/svg/total_weight.svg";
import noProduct from "../../assets/svg/products__inactive_ic.svg";
import CommonSnackbar from "../common/reusable-commponent";
import "../common/skeleton-effect.css";

// --- Constants ---
const INITIAL_ERRORS = {
  milestone_id: "",
  location_id: "",
  vehicle_id: "",
  vendor_id: "",
  stops: "",
};

const ERROR_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "start",
  fontSize: "12px",
  fontWeight: 400,
  color: "#FF4444",
  marginTop: "6px",
  marginLeft: "6px",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const TYPE_OPTIONS = [
  { value: "", label: "Select" },
  { value: "transport_stop_products.quantity", label: "Quantity" },
  { value: "transport_stop_products.weight", label: "Weight" },
  { value: "transport_stop_products.price", label: "Price" },
];

export const TripsCreate = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const adminUrl = process.env.REACT_APP_ADMIN_API_URL;

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};

  // --- Snackbar State (unified) ---
  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });
  const handleSnackbarClose = useCallback(() => setSnackbar((prev) => ({ ...prev, show: false })), []);
  const triggerSnackbar = useCallback((message, status) => {
    setSnackbar({ show: true, message, status });
  }, []);

  // --- Loading States ---
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isApplyLoading, setIsApplyLoading] = useState(false);
  const [isSortLoading, setIsSortLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isStopsLoading, setIsStopsLoading] = useState(false);

  // --- Form States ---
  const [milestoneOption, setMilestoneOption] = useState(null);
  const [VINOption, setVINOption] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState({});
  const [vendorOption, setVendorOption] = useState(null);
  const [locationOption, setLocationOption] = useState(null);

  const [customerOption, setCustomerOption] = useState([]);
  const [fromAddressOption, setFromAddressOption] = useState([]);
  const [toAddressOption, setToAddressOption] = useState([]);

  const [selectedTab, setSelectedTab] = useState("Driver");
  const [typeOption, setTypeOption] = useState(null);
  const [typeValue, setTypeValue] = useState("");

  // --- Pagination States ---
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [itemOffset, setItemOffset] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // --- Totals ---
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalKM, setTotalKM] = useState(0);
  const [totalVolume, setTotalVolume] = useState(0);
  const [totalPackage, setTotalPackage] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);

  // --- Stops ---
  const [stopsList, setStopsList] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // --- Errors ---
  const [errorMsg, setErrorMsg] = useState({ ...INITIAL_ERRORS });
  const [groupedError, setGroupedError] = useState({});

  // --- Driver ---
  const [driverList, setDriverList] = useState([]);
  const [removedDriverList, setRemovedDriverList] = useState([]);

  // --- Dates ---
  const [startDateSave, setStartDateSave] = useState(null);
  const [endDateSave, setEndDateSave] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // --- Handlers ---

  const handleSelectAll = useCallback(() => {
    setSelectAll((prev) => {
      const newSelectAll = !prev;
      setStopsList((prevStops) => {
        let nextSequence = 1;
        return prevStops.map((row) => ({
          ...row,
          selected: newSelectAll,
          sequence: newSelectAll ? nextSequence++ : "",
        }));
      });
      return newSelectAll;
    });
  }, []);

  const handleRowSelect = useCallback((index) => {
    setStopsList((prevStops) => {
      const updatedData = [...prevStops];
      const willSelect = !updatedData[index].selected;
      const maxSequence = prevStops.reduce((max, row) => (row.sequence > max ? row.sequence : max), 0);
      updatedData[index] = {
        ...updatedData[index],
        selected: willSelect,
        sequence: willSelect ? maxSequence + 1 : "",
      };
      setSelectAll(updatedData.every((row) => row.selected));
      return updatedData;
    });
  }, []);

  const getErrorMsg = useCallback(
    (index, field) => {
      if (groupedError[index]?.[field] != null) {
        const fieldLabelMap = {
          contact_id: `contacts.${index}.contact_id`,
          mobile_number: `contacts.${index}.mobile_number`,
          start_date: `contacts.${index}.start_date`,
          end_date: `contacts.${index}.end_date`,
          quantity: `products.${index}.quantity`,
          price: `products.${index}.price`,
        };
        const readableMap = {
          contact_id: "contact",
          mobile_number: "mobile_number",
          start_date: "start date",
          end_date: "end date",
          quantity: "quantity",
          price: "price",
        };
        const pattern = fieldLabelMap[field];
        const readable = readableMap[field];
        if (pattern && readable) {
          return groupedError[index][field][0]?.replace(pattern, readable) ?? "";
        }
      }
      return "";
    },
    [groupedError]
  );

  /**
   * Parses validation errors from API response and sets error state.
   */
  const handleValidationErrors = useCallback(
    (errors) => {
      if (!errors || typeof errors !== "object") return;

      const validationTemp = { ...INITIAL_ERRORS };
      Object.entries(errors).forEach(([key, value]) => {
        const rawMsg = Array.isArray(value) && value.length > 0 ? value[0] : "";
        validationTemp[key] = sanitizeInput(rawMsg);
      });
      setErrorMsg(validationTemp);
    },
    []
  );

  /**
   * Parses grouped validation errors (for products) from API response.
   */
  const handleGroupedValidationErrors = useCallback((errors) => {
    if (!errors || typeof errors !== "object") return;

    const groupedErrorTemp = {};
    Object.entries(errors).forEach(([key, value]) => {
      const match = key.match(/^products\.(\d+)\.(.+)$/);
      if (match) {
        const index = match[1];
        const field = match[2];
        if (!groupedErrorTemp[index]) groupedErrorTemp[index] = {};
        groupedErrorTemp[index][field] = value;
      }
    });
    setGroupedError(groupedErrorTemp);
  }, []);

  // --- API Functions ---

  const getStops = useCallback(async () => {
    setIsStopsLoading(true);
    try {
      const payload = {
        limit: itemsPerPage,
        skip: itemOffset * itemsPerPage,
        trip_id: id ?? "",
        type: typeOption?.value ?? "",
        value: typeValue,
        from_date: startDateSave,
        to_date: endDateSave,
        customer: customerOption.filter((item) => item.value !== null).map((item) => item.value),
        from_address: fromAddressOption.filter((item) => item.value !== null).map((item) => item.value),
        to_address: toAddressOption.filter((item) => item.value !== null).map((item) => item.value),
      };
      const response = await getStopsApi(payload);

      if (response.data.success === true) {
        const stops = response.data.stops ?? [];
        const updatedStops =
          id != null
            ? stops.map((stop) => ({
              ...stop,
              selected: stop.sequence != null && stop.sequence > 0 ? true : stop.selected || false,
            }))
            : stops;

        setStopsList(updatedStops);
        setPageCount(response.data.pages ?? 0);
        setTotalCount(response.data.totalrecords ?? 0);
        setTotalAmount(response.data.totalprice ?? 0);
        setTotalKM(response.data.totalkm ?? 0);
        setTotalVolume(response.data.totalvoulme ?? 0);
        setTotalPackage(response.data.totalpackage ?? 0);
        setTotalWeight(response.data.totalweight ?? 0);
      }
    } catch (err) {
      // Error handled by api-service
    } finally {
      setIsStopsLoading(false);
    }
  }, [itemsPerPage, itemOffset, id, typeOption, typeValue, startDateSave, endDateSave, customerOption, fromAddressOption, toAddressOption]);

  const getDefaultMilestone = useCallback(async () => {
    try {
      const response = await getDefaultMilestoneApi();
      if (response.data.success === true) {
        const defaultDetail = response.data.milestone ?? {};
        setMilestoneOption({
          value: defaultDetail.value,
          label: defaultDetail.label,
        });
      }
    } catch (err) {
      // Error handled by api-service
    }
  }, []);

  const getTripsEdit = useCallback(
    async (editId) => {
      setIsEditLoading(true);
      try {
        const response = await getTripsEditApi(editId);

        if (response.data.success === true) {
          const tripDetail = response.data.trip ?? {};
          setMilestoneOption({
            value: tripDetail.milestone_id,
            label: sanitizeInput(tripDetail.milestone),
          });
          setLocationOption({
            value: tripDetail.location_id,
            label: sanitizeInput(tripDetail.location),
          });
          setVINOption({ value: tripDetail.vehicle_id, label: sanitizeInput(tripDetail.vin) });
          setVendorOption({
            value: tripDetail.vendor_id,
            label: sanitizeInput(tripDetail.vendor),
          });
          setVehicleDetails({
            vehicle_type: sanitizeInput(tripDetail.vehicle_type),
            license_plate: sanitizeInput(tripDetail.license_plate),
          });

          const transformDriverData = (drivers, contactType) =>
            drivers.map((driver) => ({
              id: driver.id ?? 0,
              contact_type: contactType,
              contact: driver.contact_id
                ? { value: driver.contact_id, label: sanitizeInput(driver.driver_name || "") }
                : null,
              mobile_number: sanitizePhoneNumber(driver.mobile_number || ""),
              start_date: sanitizeInput(driver.start_date || ""),
              end_date: sanitizeInput(driver.end_date || ""),
              uid: uuidv4(),
            }));

          const allDrivers = [
            ...transformDriverData(response.data.driver ?? [], "Driver"),
            ...transformDriverData(response.data.co_driver ?? [], "coDriver"),
            ...transformDriverData(response.data.delivery_person ?? [], "deliveryPerson"),
          ];

          setDriverList(allDrivers);
          await getStops();
        }
      } catch (err) {
        // Error handled by api-service
      } finally {
        setIsEditLoading(false);
      }
    },
    [getStops]
  );

  const getVehicleDetails = useCallback(async (vehicleId) => {
    try {
      const response = await getVehicleDetailApi(vehicleId);
      if (response.data.success === true) {
        setVehicleDetails(response.data.vehicle ?? {});
      }
    } catch (err) {
      // Error handled by api-service
    }
  }, []);

  const submitForm = useCallback(async () => {
    setIsSaveLoading(true);
    try {
      const updatedDriverData = driverList.map((item) => ({
        ...item,
        contact_id: item.contact != null ? item.contact.value : "",
      }));

      const payloadData = {
        milestone_id: milestoneOption?.value ?? "",
        location_id: locationOption?.value ?? "",
        vehicle_id: VINOption?.value ?? "",
        vendor_id: vendorOption?.value ?? "",
        total_amount: totalAmount,
        total_kilometer: totalKM,
        total_volume: totalVolume,
        total_weight: totalWeight,
        total_package: totalPackage,
        stops: stopsList
          .filter((item) => item.selected === true && item.sequence != null && item.sequence !== "")
          .map((item) => ({
            id: item.id ?? "",
            stop_id: item.stop_id,
            sequence: item.sequence,
          })),
        contacts: updatedDriverData,
        removeId: removedDriverList,
      };

      let response = null;
      if (id == null) {
        response = await submitTripsAPI(payloadData);
      } else {
        response = await updateTripsAPI(id, payloadData);
      }

      if (response.status === 400) {
        handleValidationErrors(response.data.error);
        triggerSnackbar(sanitizeInput(response.data.message || "Validation failed. Please check the form."), false);
      } else if (response.data.success === true) {
        triggerSnackbar(sanitizeInput(response.data.message), true);
        setTimeout(() => navigate(-1), 1500);
      } else {
        handleValidationErrors(response.data.error);
        triggerSnackbar(sanitizeInput(response.data.message || "An error occurred. Please try again."), false);
      }
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.error) {
        handleGroupedValidationErrors(err.response.data.error);
        handleValidationErrors(err.response.data.error);
      }
      triggerSnackbar(sanitizeInput(err.response?.data?.message || "An error occurred. Please try again."), false);
    } finally {
      setIsSaveLoading(false);
    }
  }, [
    driverList, milestoneOption, locationOption, VINOption, vendorOption,
    totalAmount, totalKM, totalVolume, totalWeight, totalPackage,
    stopsList, removedDriverList, id, navigate, triggerSnackbar,
    handleValidationErrors, handleGroupedValidationErrors,
  ]);

  const getSortedStops = useCallback(async () => {
    setIsSortLoading(true);
    try {
      const payload = {
        stop_id: stopsList.filter((item) => item.selected === true).map((item) => item.stop_id),
      };
      const response = await getSortedStopsApi(payload);

      if (response.data.success === true) {
        const sortedList = response.data.sorted_stops ?? [];
        const updatedArray = stopsList.map((item) => {
          const match = sortedList.find((el) => el.id === item.stop_id);
          if (match) {
            return { ...item, sequence: match.sequence, error: match.error || null };
          }
          return { ...item, error: null };
        });
        setStopsList(updatedArray ?? []);
        triggerSnackbar(sanitizeInput(response.data.message || "Stops sorted successfully"), true);
      } else {
        triggerSnackbar(sanitizeInput(response.data.message || "Stops sorting failed"), false);
      }
    } catch (err) {
      triggerSnackbar(sanitizeInput(err.response?.data?.message || "Stops sorting failed"), false);
    } finally {
      setIsSortLoading(false);
    }
  }, [stopsList, triggerSnackbar]);

  const handleDateChange = useCallback(({ start, end }) => {
    setStartDate(start);
    setEndDate(end);
    setStartDateSave(start ? format(start, "yyyy-MM-dd") : null);
    setEndDateSave(end ? format(end, "yyyy-MM-dd") : null);
  }, []);

  const handleDriverFieldChange = useCallback(
    (uid, field, value) => {
      setDriverList((prevList) =>
        prevList.map((item) => (item.uid === uid ? { ...item, [field]: value } : item))
      );
    },
    []
  );

  const handleRemoveDriver = useCallback(
    (row) => {
      if (row.id !== null && row.id !== "" && row.id !== 0) {
        setRemovedDriverList((prev) => [...prev, row.id]);
      }
      setDriverList((prevList) => prevList.filter((item) => item.uid !== row.uid));
    },
    []
  );

  const handleAddDriver = useCallback(() => {
    setDriverList((prevList) => [
      ...prevList,
      {
        id: 0,
        contact_type: selectedTab === "Driver" ? "Driver" : selectedTab === "coDriver" ? "coDriver" : "deliveryPerson",
        contact: null,
        mobile_number: "",
        start_date: "",
        end_date: "",
        uid: uuidv4(),
      },
    ]);
  }, [selectedTab]);

  const handleResetFilters = useCallback(() => {
    setCustomerOption([]);
    setFromAddressOption([]);
    setToAddressOption([]);
    setTypeOption({ value: "", label: "Select" });
    setTypeValue("");
    setStartDateSave(null);
    setEndDateSave(null);
    setStartDate(null);
    setEndDate(null);
  }, []);

  // --- Effects ---

  useEffect(() => {
    if (id != null) {
      getTripsEdit(id);
    } else {
      getStops();
      getDefaultMilestone();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    getStops();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage, itemOffset]);

  // --- Render ---

  return (
    <div>
      {/* Header */}
      <div className="px-2 d-flex align-items-center justify-content-between">
        <div className="d-flex gap-3 my-3">
          <MoveLeft className="cursor-pointer" onClick={() => navigate(-1)} />
          <p className="fw-semibold m-0">New trip</p>
        </div>
        <div className="d-flex gap-3">
          <Button
            className="cursor-pointer bg-white rounded-pill px-4 py-2 btn-border-grey fs-15 fw-500"
            style={{ border: "1px solid #D4DDE7", color: "#212129" }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            className={`cursor-pointer dark-purple-btn ${isSaveLoading ? "disabled-btn" : ""} rounded-pill py-2 px-4 d-flex justify-content-center align-items-center`}
            onClick={submitForm}
            style={{ minWidth: "100px" }}
          >
            {isSaveLoading ? <Spinner animation="border" size="sm" /> : id != null ? "Update" : "Save"}
          </Button>
        </div>
      </div>

      <div className="col-12">
        <div className="row mb-2">
          {/* Trip Details Card */}
          <div className="col-12 col-md-6 ">
            <div className="bg-white rounded-4 d-flex flex-column align-items-start px-2 py-2">
              <div className="fs-14 fw-700" style={{ color: "#28303F" }}>
                Trip details
              </div>

              {isEditLoading ? (
                <>
                  <div className="mt-2 col-12 d-flex flex-column align-items-start">
                    <div className="skeleton skeleton-text-sm" style={{ width: "80px", marginBottom: "8px" }} />
                    <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                  </div>
                  <div className="mt-2 col-12 d-flex flex-column align-items-start">
                    <div className="skeleton skeleton-text-sm" style={{ width: "80px", marginBottom: "8px" }} />
                    <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-2 col-12 d-flex flex-column align-items-start">
                    <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                      Milestone <span className="error-text">*</span>
                    </div>
                    <div className="w-100">
                      <CommonDropdown
                        apiUrl={`${apiUrl}milestone/autosuggestion`}
                        placeholder="Select"
                        value={milestoneOption}
                        onSelect={(item) => setMilestoneOption(item)}
                      />
                    </div>
                    <span style={ERROR_STYLE}>{errorMsg.milestone_id}</span>
                  </div>

                  <div className="mt-2 col-12 d-flex flex-column align-items-start">
                    <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                      Location <span className="error-text">*</span>
                    </div>
                    <div className="w-100">
                      <CommonDropdown
                        apiUrl={`${adminUrl}get_location`}
                        placeholder="Select"
                        value={locationOption}
                        onSelect={(item) => setLocationOption(item)}
                      />
                    </div>
                    <span style={ERROR_STYLE}>{errorMsg.location_id}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Vehicle Details Card */}
          <div className="col-12 col-md-6 pe-0">
            <div className="bg-white rounded-4 d-flex flex-column align-items-start px-2 py-2">
              <div className="fs-14 fw-700" style={{ color: "#28303F" }}>
                Vehicle details
              </div>

              {isEditLoading ? (
                <>
                  <div className="col-12">
                    <div className="row">
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start">
                        <div className="skeleton skeleton-text-sm" style={{ width: "60px", marginBottom: "8px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                      </div>
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start ps-0">
                        <div className="skeleton skeleton-text-sm" style={{ width: "100px", marginBottom: "8px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="row">
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start">
                        <div className="skeleton skeleton-text-sm" style={{ width: "100px", marginBottom: "8px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                      </div>
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start ps-0">
                        <div className="skeleton skeleton-text-sm" style={{ width: "80px", marginBottom: "8px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px", borderRadius: "8px" }} />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-12">
                    <div className="row">
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start">
                        <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                          VIN <span className="error-text">*</span>
                        </div>
                        <div className="w-100">
                          <CommonDropdown
                            apiUrl={`${apiUrl}vehicle/autosuggestion`}
                            placeholder="Select"
                            value={VINOption}
                            onSelect={(item) => {
                              setVINOption(item);
                              getVehicleDetails(item.value);
                            }}
                          />
                        </div>
                        <span style={ERROR_STYLE}>{errorMsg.vehicle_id}</span>
                      </div>
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start ps-0">
                        <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                          License Plate
                        </div>
                        <div className="w-100 rounded-3 px-2 d-flex align-items-center justify-content-start" style={{ height: "40px", background: "#F8F8F8" }}>
                          <span className="fs-14 fw-300" style={{ color: "#8790A1" }}>
                            {vehicleDetails.license_plate}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="row">
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start">
                        <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                          Vehicle Type
                        </div>
                        <div className="w-100 rounded-3 px-2 d-flex align-items-center justify-content-start" style={{ height: "40px", background: "#F8F8F8" }}>
                          <span className="fs-14 fw-300" style={{ color: "#8790A1" }}>
                            {vehicleDetails.vehicle_type}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start ps-0">
                        <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                          Vendor
                        </div>
                        <div className="w-100">
                          <CommonDropdown
                            apiUrl={`${apiUrl}vendor/autosuggestion`}
                            placeholder="Select"
                            value={vendorOption}
                            onSelect={(item) => setVendorOption(item)}
                          />
                        </div>
                        <span style={ERROR_STYLE}>{errorMsg.vendor_id}</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Driver / Co-Driver / Delivery Person Section */}
        <div className="bg-white rounded-4 p-3">
          <div className="row">
            <div className="col-12">
              <div className="row">
                <div className="w-100 mb-1 d-flex align-items-center justify-content-start gap-2">
                  {["Driver", "coDriver", "deliveryPerson"].map((tab) => (
                    <div
                      key={tab}
                      className={`cursor-pointer d-flex align-items-center justify-content-center rounded-3 px-2 fs-13 ${selectedTab === tab ? "fw-500 active-bottom-bar" : "fw-400"}`}
                      style={{
                        height: "32px",
                        minWidth: "110px",
                        color: selectedTab === tab ? "#FFFFFF" : "#172B4D",
                        background: selectedTab === tab ? "#3469E7" : "#F5F5F5",
                      }}
                      onClick={() => setSelectedTab(tab)}
                    >
                      {tab === "Driver" ? "Driver" : tab === "coDriver" ? "Co-Driver" : "Delivery Person"}
                    </div>
                  ))}
                </div>

                <>
                  <div style={{ overflowY: "visible" }}>
                    <div className="mt-2 sticky-table-wrapper">
                      <table
                        className="stop-table"
                        border="1"
                        cellPadding="10"
                        style={{ borderCollapse: "collapse", width: "100%", border: "1px solid #EBEBEB" }}
                      >
                        <thead
                          style={{
                            height: "48px",
                            color: "#8790A1",
                            fontSize: "11px",
                            fontWeight: "400",
                            textAlign: "left",
                          }}
                        >
                          <tr>
                            <th className="ps-2">{selectedTab === "Driver" ? "DRIVER" : selectedTab === "coDriver" ? "CO-DRIVER" : "DELIVER PERSON"}</th>
                            <th className="ps-2">MOBILE NUMBER</th>
                            <th className="ps-2">STARTS ON</th>
                            <th className="ps-2">ENDS ON</th>
                            <th className="ps-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {driverList
                            .filter((row) => row.contact_type === selectedTab)
                            .map((row, index1) => (
                              <tr
                                key={row.uid}
                                style={{ height: "60px", position: "relative", overflow: "visible" }}
                              >
                                <td className="px-2 py-1" style={{ position: "relative", overflow: "visible" }}>
                                  <div className="pe-3 py-1" style={{ width: "200px" }}>
                                    <CommonDropdown
                                      apiUrl={`${adminUrl}get_driver`}
                                      placeholder="Select"
                                      value={row.contact}
                                      onSelect={(item) => handleDriverFieldChange(row.uid, "contact", item)}
                                    />
                                    <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "contact_id")}</span>
                                  </div>
                                </td>
                                <td className="px-2 py-1">
                                  <input
                                    type="text"
                                    className="form-control rounded-3 vendor-form-input"
                                    id={`mobile${index1}`}
                                    placeholder="Enter here"
                                    value={row.mobile_number}
                                    onChange={(e) =>
                                      handleDriverFieldChange(
                                        row.uid,
                                        "mobile_number",
                                        sanitizePhoneNumber(e.target.value)
                                      )
                                    }
                                    style={{ borderColor: getErrorMsg(index1, "mobile_number") !== "" ? "red" : "#EBEBEB" }}
                                  />
                                  <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "mobile_number")}</span>
                                </td>

                                <td className="px-2 py-1">
                                  <input
                                    type="datetime-local"
                                    className="date-time-input"
                                    value={row.start_date}
                                    onChange={(e) => handleDriverFieldChange(row.uid, "start_date", e.target.value)}
                                    style={{ padding: "6px", borderRadius: "6px" }}
                                  />
                                  <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "start_date")}</span>
                                </td>

                                <td className="px-2 py-1" style={{ width: "150px" }}>
                                  <input
                                    type="datetime-local"
                                    className="date-time-input"
                                    value={row.end_date}
                                    onChange={(e) => handleDriverFieldChange(row.uid, "end_date", e.target.value)}
                                    style={{ padding: "6px", borderRadius: "6px" }}
                                  />
                                  <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "end_date")}</span>
                                </td>

                                <td>
                                  <div
                                    style={{ cursor: "pointer", marginTop: "14px" }}
                                    onClick={() => handleRemoveDriver(row)}
                                    className="mx-2 ps-2 d-flex align-items-center justify-content-center"
                                  >
                                    <img src={deleteIcon} alt="delete" />
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="col-xl-12 mt-2">
                    <Button
                      className="cursor-pointer border-0 rounded-pill py-2 px-4 d-flex justify-content-center align-items-center"
                      onClick={handleAddDriver}
                      style={{
                        minWidth: "100px",
                        background: "#E6E4FF",
                        color: "#212129",
                        fontWeight: "600",
                        fontSize: "13px",
                      }}
                    >
                      Add driver
                    </Button>
                  </div>
                </>
              </div>
            </div>
          </div>
        </div>

        {/* Stops Filter & Table Section */}
        <div className="col-12 bg-white rounded-4 px-0">
          <div className="m-2 bg-white rounded-4 d-flex flex-column align-items-start px-2 py-2">
            <div className="w-100 d-flex align-items-center justify-content-between">
              <div className="fs-14 fw-700" style={{ color: "#28303F" }}>
                Filter Stops
              </div>
              <div className="d-flex gap-3">
                <Button
                  className="cursor-pointer bg-white rounded-3 px-3 py-2 btn-border-grey fs-15 fw-500"
                  style={{ border: "1px solid #D4DDE7", color: "#212129" }}
                  onClick={handleResetFilters}
                >
                  Reset
                </Button>
                <Button
                  className={`cursor-pointer dark-purple-btn ${isApplyLoading ? "disabled-btn" : ""} rounded-3 py-2 px-2 d-flex justify-content-center align-items-center fs-15 fw-500`}
                  onClick={async () => {
                    setIsApplyLoading(true);
                    await getStops();
                    setIsApplyLoading(false);
                  }}
                  style={{ minWidth: "100px" }}
                >
                  {isApplyLoading ? <Spinner animation="border" size="sm" /> : "Apply"}
                </Button>
              </div>
            </div>
            <div className="col-12">
              <div className="row">
                <div className="mt-2 col-12 col-md-4 d-flex flex-column align-items-start">
                  <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                    Customer
                  </div>
                  <div className="w-100">
                    <CommonAsyncMultiSelect
                      apiUrl={`${apiUrl}customer/autosuggestion`}
                      value={customerOption}
                      onChange={(selectedOptions) => setCustomerOption(selectedOptions)}
                      placeholder="Search customer..."
                    />
                  </div>
                </div>

                <div className="mt-2 col-12 col-md-4 d-flex flex-column align-items-start">
                  <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                    From Address
                  </div>
                  <div className="w-100">
                    <CommonAsyncMultiSelect
                      apiUrl={`${apiUrl}stops/from_address`}
                      value={fromAddressOption}
                      onChange={(selectedOptions) => setFromAddressOption(selectedOptions)}
                      placeholder="Search from address..."
                    />
                  </div>
                </div>

                <div className="mt-2 col-12 col-md-4 d-flex flex-column align-items-start">
                  <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                    To Address
                  </div>
                  <div className="w-100">
                    <CommonAsyncMultiSelect
                      apiUrl={`${apiUrl}stops/to_address`}
                      value={toAddressOption}
                      onChange={(selectedOptions) => setToAddressOption(selectedOptions)}
                      placeholder="Search to address..."
                    />
                  </div>
                </div>

                <div className="mt-2 col-12 col-md-4 d-flex flex-column align-items-start">
                  <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                    Date range
                  </div>
                  <div className="w-100">
                    <CustomDateRangePicker onChange={handleDateChange} startDate={startDate} endDate={endDate} />
                  </div>
                </div>

                <div className="mt-2 col-12 col-md-4 d-flex flex-column align-items-start">
                  <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                    Type
                  </div>
                  <div className="w-100">
                    <CommonStaticDropdown
                      items={TYPE_OPTIONS}
                      placeholder="Select"
                      value={typeOption}
                      enableSearch={false}
                      onSelect={(item) => setTypeOption(item)}
                    />
                  </div>
                </div>

                <div className="mt-2 col-12 col-md-4 d-flex flex-column align-items-start">
                  <div className="form-label fs-12 fw-400 my-0 mb-1" style={{ color: "#28303F" }}>
                    Value
                  </div>
                  <div className="w-100">
                    <input
                      type="text"
                      className="form-control rounded-3 vendor-form-input"
                      id="type-value"
                      placeholder="Enter here"
                      value={typeValue}
                      onChange={(e) => setTypeValue(sanitizeNumericInput(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="d-flex align-items-center justify-content-center gap-2">
            <div className="col-6 col-md-4 col-lg-2 rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#FFFAE5" }}>
              <img src={totalAmtIc} alt="total amount" />
              <div className="ms-2">
                <div className="fs-12 fw-400" style={{ color: "#8790A1", textAlign: "start" }}>Total Amount</div>
                <div className="fs-16 fw-600 mt-1" style={{ color: "#172B4D", textAlign: "start" }}>{totalAmount}</div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2 rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#EAFFF3" }}>
              <img src={totalKMIc} alt="totalKMIc" />
              <div className="ms-2">
                <div className="fs-12 fw-400" style={{ color: "#8790A1", textAlign: "start" }}>Total Kilometer</div>
                <div className="fs-16 fw-600 mt-1" style={{ color: "#172B4D", textAlign: "start" }}>{totalKM}</div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2 rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#F0F1FF" }}>
              <img src={totalVolumeIc} alt="totalVolumeIc" />
              <div className="ms-2">
                <div className="fs-12 fw-400" style={{ color: "#8790A1", textAlign: "start" }}>Total Volume</div>
                <div className="fs-16 fw-600 mt-1" style={{ color: "#172B4D", textAlign: "start" }}>{totalVolume}</div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2 rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#FFEBEB" }}>
              <img src={totalPackageIc} alt="totalPackageIc" />
              <div className="ms-2">
                <div className="fs-12 fw-400" style={{ color: "#8790A1", textAlign: "start" }}>Total Package</div>
                <div className="fs-16 fw-600 mt-1" style={{ color: "#172B4D", textAlign: "start" }}>{totalPackage}</div>
              </div>
            </div>
            <div className="col-6 col-md-4 col-lg-2 rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#F7F2FF" }}>
              <img src={totalWeightIc} alt="totalWeightIc" />
              <div className="ms-2">
                <div className="fs-12 fw-400" style={{ color: "#8790A1", textAlign: "start" }}>Total Weight</div>
                <div className="fs-16 fw-600 mt-1" style={{ color: "#172B4D", textAlign: "start" }}>{totalWeight}</div>
              </div>
            </div>
          </div>

          {/* Stops Table Header */}
          <div className="d-flex align-items-center justify-content-between px-2">
            <div className="fs-14 fw-700" style={{ color: "#28303F" }}>
              Stops ({totalCount})
            </div>

            <div className="my-2 d-flex align-items-center gap-2">
              <Dropdown>
                <span className="pagination-color">Records per page: &nbsp;</span>
                <Dropdown.Toggle id="dropdown-basic" className="bg-transparent pagination-color border-0 custom-dropdown-toggle p-0">
                  <span className="bg-white textcolor no-of-rows rounded-3 px-2 py-2" style={{ border: "1px solid #EBEBEB" }}>
                    {itemsPerPage} <DownArrowIc />
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="my-2" style={{ minWidth: "unset", width: "30%" }}>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <Dropdown.Item
                      key={size}
                      onClick={() => setItemsPerPage(size)}
                      active={itemsPerPage === size}
                      className="text-center"
                    >
                      {size}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "16px" }}>
                <span>
                  {itemOffset + 1} of {pageCount}
                </span>
                <button
                  className="bg-white"
                  onClick={() => {
                    if (itemOffset > 0) setItemOffset(itemOffset - 1);
                  }}
                  disabled={itemOffset === 0}
                  style={{ cursor: "pointer", width: "32px", height: "32px", borderRadius: "100px", border: "1px solid #EBEBEB" }}
                >
                  &lt;
                </button>
                <button
                  className="bg-white"
                  onClick={() => {
                    if (itemOffset < pageCount - 1) setItemOffset(itemOffset + 1);
                  }}
                  disabled={pageCount === 0 || itemOffset === pageCount - 1}
                  style={{ cursor: "pointer", width: "32px", height: "32px", borderRadius: "100px", border: "1px solid #EBEBEB" }}
                >
                  &gt;
                </button>
              </div>

              <Button
                className={`cursor-pointer dark-purple-btn ${isSortLoading ? "disabled-btn" : ""} rounded-3 py-2 px-2 d-flex justify-content-center align-items-center fs-15 fw-500`}
                onClick={getSortedStops}
                style={{ minWidth: "80px" }}
              >
                {isSortLoading ? <Spinner animation="border" size="sm" /> : "Sort Stops"}
              </Button>
            </div>
          </div>

          <span style={ERROR_STYLE}>{errorMsg.stops}</span>

          {/* Stops Table */}
          <div
            className="px-2"
            style={{ width: "100%", overflowX: "auto", overflowY: "visible", whiteSpace: "nowrap" }}
          >
            <div style={{ overflowX: "auto", width: "100%" }}>
              <table border="1" cellPadding="10" style={{ minWidth: "800px", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th className="trip-table-header">
                      <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                    </th>
                    <th className="trip-table-header min-width-120">SEQ.NO</th>
                    <th className="trip-table-header min-width-120">CUSTOMER NAME</th>
                    <th className="trip-table-header min-width-120">STOP ID</th>
                    <th className="trip-table-header min-width-120">PRODUCT NAME</th>
                    <th className="trip-table-header min-width-120">FROM ADDRESS</th>
                    <th className="trip-table-header min-width-120">TO ADDRESS</th>
                    <th className="trip-table-header min-width-120">UNIT</th>
                    <th className="trip-table-header min-width-120">WEIGHT</th>
                    <th className="trip-table-header min-width-120">QUANTITY</th>
                    <th className="trip-table-header min-width-120">PRICE</th>
                    <th className="trip-table-header min-width-120">DATE TIME</th>
                  </tr>
                </thead>
                <tbody>
                  {isStopsLoading ? (
                    Array.from({ length: 5 }).map((_, idx) => (
                      <tr key={`skeleton-${idx}`}>
                        <td className="trip-table-row">
                          <div className="skeleton" style={{ width: "16px", height: "16px", borderRadius: "3px" }} />
                        </td>
                        <td className="trip-table-row text-center">
                          <div className="skeleton" style={{ width: "60px", height: "30px", margin: "0 auto" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "100px", height: "14px" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "80px", height: "14px" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "120px", height: "14px" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "150px", height: "14px" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "150px", height: "14px" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "60px", height: "14px" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "50px", height: "14px" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "40px", height: "14px" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "70px", height: "14px" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "60px", height: "14px" }} />
                        </td>
                        <td className="trip-table-row">
                          <div className="skeleton skeleton-text" style={{ width: "100px", height: "14px" }} />
                        </td>
                      </tr>
                    ))
                  ) : (stopsList ?? []).length === 0 ? (
                    <tr>
                      <td colSpan="13" style={{ textAlign: "center", padding: "60px 20px" }}>
                        <div className="d-flex flex-column align-items-center justify-content-center">
                          <div
                            className="mb-3 d-flex align-items-center justify-content-center"
                            style={{ width: "80px", height: "80px", borderRadius: "50%", background: "#F5F8FA" }}
                          >
                            <img src={noProduct} className="w-100 h-100" alt="No Data Found" />
                          </div>
                          <div className="fs-16 fw-600 mb-2" style={{ color: "#28303F" }}>
                            No Stops Found
                          </div>
                          <div className="fs-13 fw-400" style={{ color: "#8790A1", maxWidth: "400px" }}>
                            {locationOption ? "No stops available for the selected filters. Try adjusting your search criteria." : "Please select a location to view available stops."}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    (stopsList ?? []).map((row, index) => (
                      <tr key={row.stop_id || index}>
                        <td className="trip-table-row">
                          <input type="checkbox" checked={row.selected} onChange={() => handleRowSelect(index)} />
                        </td>
                        <td className="trip-table-row text-center">
                          <input
                            type="number"
                            value={row.sequence}
                            onChange={(e) => {
                              setStopsList((prevStops) => {
                                const updated = [...prevStops];
                                updated[index] = {
                                  ...updated[index],
                                  sequence: sanitizeNumericInput(e.target.value),
                                };
                                return updated;
                              });
                            }}
                            style={{
                              width: "60px",
                              border: "1px solid #EBEBEB",
                              borderColor: row.error ? "#FF4444" : "#EBEBEB",
                            }}
                            disabled={!!row.error}
                          />
                          {row.error && (
                            <div
                              className="fs-8 fw-100"
                              style={{
                                color: "#FF4444",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "50px",
                              }}
                              title={sanitizeInput(row.error)}
                            >
                              {sanitizeInput(row.error)}
                            </div>
                          )}
                        </td>
                        <td className="trip-table-row">{sanitizeInput(row.customer_code)} - {sanitizeInput(row.customer_name)}</td>
                        <td className="trip-table-row">{sanitizeInput(row.stop_code)}</td>
                        <td className="trip-table-row">{sanitizeInput(row.product_name)}</td>
                        <td className="trip-table-row">{sanitizeInput(row.from_address)}</td>
                        <td className="trip-table-row">{sanitizeInput(row.to_address)}</td>
                        <td className="trip-table-row">{sanitizeInput(row.unit)}</td>
                        <td className="trip-table-row">{row.weight}</td>
                        <td className="trip-table-row">{row.quantity}</td>
                        <td className="trip-table-row">{row.price}</td>
                        <td className="trip-table-row">{sanitizeInput(row.created_date)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Snackbar */}
      <CommonSnackbar
        show={snackbar.show}
        onClose={handleSnackbarClose}
        message={snackbar.message}
        isSuccess={snackbar.status}
      />
    </div>
  );
};
